import { z } from 'zod'

import { ReviewSortKey, type ReviewLevel } from '@/client'
import { plainSearch } from '@/components/Ams/shared/plainSearch'
import { pruned } from '@/lib/filters'

import { defaultSortFor, DEFAULT_SORT_DIR } from './sortKeys'

/**
 * The Review AMS Data page's URL state.
 *
 * VentaHub kept everything in component state (no search params,
 * `vh-fe-review` §1); VentaWiz keeps every view control in the URL (rules E8),
 * so the keys are VentaHub's request field names: the applied filters
 * (`ReviewAmsDataPage.tsx:148-155`), the tab and cross-drill (:184-191), the
 * table's page, size and sort (:192-195), the open row (`ReviewTable.tsx:225`)
 * and the Negatives tab's own paging and sort (`NegativesTable.tsx:87-91`).
 *
 * Every key is optional with `.catch(undefined)`: a query string is user
 * input, and a bad value falls back to its default rather than failing the
 * page (rules E9). The router JSON-parses each value, so text that looks like
 * a number arrives as one and is turned back into text here.
 */

/** The page's tabs, in VentaHub's order (`LEVEL_TABS`, :78-86) without Placements (hidden). */
export const REVIEW_TABS = [
  'ad_group',
  'ad_product',
  'target',
  'search_term',
  'negative',
  'alt_product',
] as const
export type ReviewTab = (typeof REVIEW_TABS)[number]

/** VentaHub's page sizes (`ReviewTable.tsx:537`). */
export const PAGE_SIZES = [25, 50, 100, 250] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 50

/** The Negatives headers that sort (`NegativesTable.tsx:69-76`). */
export const NEGATIVE_SORT_KEYS = ['keyword_text', 'status', 'date', 'ad_group_count'] as const
export type NegativeSortColumn = (typeof NEGATIVE_SORT_KEYS)[number]
/** Latest-touched first: the default read is "what changed recently" (NegativesTable.tsx:89-91). */
export const DEFAULT_NEGATIVE_SORT: NegativeSortColumn = 'date'

/** Text the router JSON-parsed: `?search=2024` arrives as the number 2024. */
function scalarText(value: unknown): unknown {
  return typeof value === 'number' || typeof value === 'boolean' ? String(value) : value
}

/** A number the router left as text (`?store_id=` from a hand-written link). */
function toNumber(value: unknown): unknown {
  return typeof value === 'string' && value.trim() !== '' ? Number(value) : value
}

/** A JSON array, a lone id or a CSV string, as a list of ids; anything else in it is dropped. */
function toIdList(value: unknown): unknown {
  const items: unknown[] = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [value]
  return items
    .map(toNumber)
    .filter((item) => typeof item === 'number' && Number.isInteger(item) && item >= 0)
}

const id = z.preprocess(toNumber, z.number().int().nonnegative()).optional().catch(undefined)
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)
const text = (max: number) =>
  z.preprocess(scalarText, z.string().max(max)).optional().catch(undefined)
const dir = z.enum(['asc', 'desc']).optional().catch(undefined)
const page = z.preprocess(toNumber, z.number().int().positive()).optional().catch(undefined)
const pageSize = z
  .preprocess(toNumber, z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)]))
  .optional()
  .catch(undefined)

export const reviewAmsSearchSchema = z.object({
  /** The applied store; absent until the first store is picked for the reader (RA:170-174). */
  store_id: id.describe(
    'The store reviewed, by id. When absent the page picks a default store once the counts load and writes it.',
  ),
  /** The applied periods; absent means the defaults, last 7 days and the 7 before (RA:131-140). */
  current_start: day.describe(
    'First day of the current period, YYYY-MM-DD; default six days before today.',
  ),
  current_end: day.describe('Last day of the current period, YYYY-MM-DD; default today.'),
  previous_start: day.describe(
    'First day of the previous period, YYYY-MM-DD; default the same length immediately before the current one.',
  ),
  previous_end: day.describe(
    'Last day of the previous period, YYYY-MM-DD; default the day before the current period starts.',
  ),
  /** The applied brands in click order; absent means every brand (RA:269-289). */
  brand_ids: z
    .preprocess(toIdList, z.array(z.number().int().nonnegative()))
    .optional()
    .catch(undefined)
    .describe('Brands to include, by id, CSV (10,11); absent or every brand means all brands.'),
  ad_group_type: z
    .enum(['SP', 'SB', 'SD'])
    .optional()
    .catch(undefined)
    .describe('Ad type to narrow to (SP, SB or SD); absent means all.'),
  search: text(200).describe(
    'Applied search; what it matches follows the tab (ad group or campaign, ASIN or SKU, target, term\u2026).',
  ),
  /** The active tab; Placements (hidden) and anything else fall back to Ad Groups. */
  tab: z
    .enum(REVIEW_TABS)
    .optional()
    .catch(undefined)
    .describe('The review level the table lists; default ad_group.'),
  /** The cross-drilled ad group (`pk_ag_id`) and the name its chip shows (RA:191, 343-347). */
  drill: id.describe(
    'Ad group (master id) the table is cross-drilled into; absent means every ad group.',
  ),
  drill_name: text(500).describe('Name of the cross-drilled ad group, shown on its chip.'),
  sort_by: z
    .nativeEnum(ReviewSortKey)
    .optional()
    .catch(undefined)
    .describe(
      'Column the main table is sorted by; default curr_spend (curr_sales on Alt Products).',
    ),
  sort_dir: dir.describe('Sort direction of the main table; default desc.'),
  page: page.describe('Page of the main table, 1-based; default 1.'),
  page_size: pageSize.describe('Rows per page of the main table; default 50.'),
  /** The expanded row's key (`ReviewTable.tsx:119-130`). */
  open: text(1000).describe(
    'Key of the expanded row (ag-123, st-term\u2026), which opens its review panel.',
  ),
  neg_page: page.describe('Page of the Negatives table, 1-based; default 1.'),
  neg_page_size: pageSize.describe('Rows per page of the Negatives table; default 50.'),
  neg_sort_by: z
    .enum(NEGATIVE_SORT_KEYS)
    .optional()
    .catch(undefined)
    .describe('Column the Negatives table is sorted by; default date.'),
  neg_sort_dir: dir.describe('Sort direction of the Negatives table; default desc.'),
})

export type ReviewAmsSearch = z.infer<typeof reviewAmsSearchSchema>

/** The tab a URL shows. */
export function tabOf(search: ReviewAmsSearch): ReviewTab {
  return search.tab ?? 'ad_group'
}

/** The metric level a tab reads, or `null` on Negatives, which has its own endpoint (RA:185-190). */
export function metricLevelOf(tab: ReviewTab): ReviewLevel | null {
  return tab === 'negative' ? null : tab
}

/**
 * The URL without its resting values: `lib/filters#pruned` (undefined, empty,
 * `page: 1`) plus the default tab, the tab's default sort, page size 50, and
 * the Negatives defaults. The periods are never pruned — the default is
 * relative to today, and the applied period is what a shared link is about.
 */
export function pruneReviewAmsSearch(search: ReviewAmsSearch): ReviewAmsSearch {
  const out = { ...search }
  const tab = tabOf(search)
  const sort = defaultSortFor(metricLevelOf(tab) ?? 'ad_group')
  if (out.tab === 'ad_group') out.tab = undefined
  if (out.sort_by === sort.sortBy && (out.sort_dir ?? DEFAULT_SORT_DIR) === sort.sortDir) {
    out.sort_by = undefined
  }
  if (out.sort_by === undefined && out.sort_dir === DEFAULT_SORT_DIR) out.sort_dir = undefined
  if (out.page_size === DEFAULT_PAGE_SIZE) out.page_size = undefined
  if (out.neg_page === 1) out.neg_page = undefined
  if (out.neg_page_size === DEFAULT_PAGE_SIZE) out.neg_page_size = undefined
  if (out.neg_sort_by === DEFAULT_NEGATIVE_SORT && out.neg_sort_dir !== 'asc') {
    out.neg_sort_by = undefined
  }
  if (out.neg_sort_by === undefined && out.neg_sort_dir === 'desc') out.neg_sort_dir = undefined
  return pruned(out)
}

/**
 * The route's search middleware: the brands written as AMS View writes its
 * ids, `brand_ids=10,11`, and a numeric search or name unquoted, rather than
 * the router's JSON (`shared/plainSearch.ts`). The schema above reads both.
 */
export const plainReviewAmsSearch = plainSearch<ReviewAmsSearch>({
  idLists: ['brand_ids'],
  wholeNumberTexts: ['search', 'drill_name', 'open'],
})
